/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Skeleton, Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  buildBaseFixedMixLookup,
  buildBaseMaterialChemistryLookup,
  buildBaseMaterialLimitLookup,
  materialChemistryKey,
  materialLimitKey,
} from "components/search/input/DifferencePanel/baseLookups";
import {
  DiffCategory,
  PanelSection,
} from "components/search/input/DifferencePanel/common";
import { DifferencePanelPeriod } from "components/search/input/DifferencePanel/DifferencePanelPeriod";
import { FixedMixOverrideDiffItem } from "components/search/input/DifferencePanel/FixedMixOverrideDiffItem";
import { InventoryItemConfigFactory } from "components/search/input/DifferencePanel/InventoryItemConfigFactory";
import { MaterialChemistryDiffItem } from "components/search/input/DifferencePanel/MaterialChemistryDiffItem";
import { MaterialLimitDiffItem } from "components/search/input/DifferencePanel/MaterialLimitDiffItem";
import { MiscParamsConfigFactory } from "components/search/input/DifferencePanel/MiscParamsConfigFactory";
import { PhysicalParametersConfigFactory } from "components/search/input/DifferencePanel/PhysicalParametersConfigFactory";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useIsAdmin, useTenant } from "hooks/settings";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  FixedMixOverride,
  NameBasedMaterialChemistry,
  NameBasedMaterialLimit,
  NameBasedSearchDiff,
  useGetNameBasedSearchDiffQuery,
  useGetSearchContextQuery,
  useGetSearchQuery,
  useListMaterialConstraintClassesQuery,
} from "store/api/generatedApi";

interface Props {
  targetSearchId: number;
  sourceSearchId: number;
}

interface BaseLookups {
  materialLimits: Map<string, NameBasedMaterialLimit>;
  materialChemistries: Map<string, NameBasedMaterialChemistry>;
  fixedMixes: Map<string, FixedMixOverride>;
}

const isNameBasedLimitNullified = (
  limit: NameBasedMaterialLimit | null
): boolean =>
  limit?.bounds.every(
    (bound) =>
      bound.min_mass == null &&
      bound.max_mass == null &&
      bound.soft_min_mass == null &&
      bound.soft_max_mass == null
  ) ?? false;

const DiffResults = ({
  diff,
  sourceBaseLookups,
  targetBaseLookups,
}: {
  diff: NameBasedSearchDiff | undefined;
  sourceBaseLookups: BaseLookups;
  targetBaseLookups: BaseLookups;
}) => {
  const anyDifferenceExceptPeriods = diff
    ? Object.entries(diff).some(([key, value]) => {
        if (key === "periods") {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null;
      })
    : false;
  const hasAnyDifference =
    anyDifferenceExceptPeriods || (diff?.periods?.length ?? 0) > 0;

  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber();
  const formatNumber2DP = formatNumber(2);
  const formatNumber2DPFixed = formatNumber(2, true);
  const isAdmin = useIsAdmin();

  const [
    inventoryAttributesConfig,
    miscAttributesConfig,
    physicalParametersAttributesConfig,
  ] = useMemo(() => {
    const utils = {
      t,
      units,
      formatNumber2DP,
      formatNumber2DPFixed,
      isAdmin,
    };
    return [
      InventoryItemConfigFactory(utils),
      MiscParamsConfigFactory(utils),
      PhysicalParametersConfigFactory(utils),
    ];
  }, [t, units, formatNumber2DP, formatNumber2DPFixed, isAdmin]);

  if (!hasAnyDifference || !diff)
    return (
      <Typography variant="h6" sx={{ pb: 2 }}>
        {t("noDifferencesFound")}
      </Typography>
    );

  return (
    <Stack
      direction="column"
      sx={{
        mx: -2,
        px: 2,
        overflow: "auto",
        maxHeight: "90dvh",
      }}
      gap={2}
    >
      {anyDifferenceExceptPeriods ? (
        <Stack gap={2}>
          {diff.inventory && diff.inventory.length > 0 ? (
            <PanelSection title={t("inventoryInHand")}>
              {diff.inventory.map((inventoryItem) => (
                <DiffCategory
                  key={inventoryItem.key}
                  diff={inventoryItem}
                  config={inventoryAttributesConfig}
                />
              ))}
            </PanelSection>
          ) : null}
          {diff.misc_params ? (
            <PanelSection title={t("miscellaneous")}>
              <DiffCategory
                hideKeys
                diff={diff.misc_params}
                config={miscAttributesConfig}
              />
            </PanelSection>
          ) : null}
          {diff.physical_parameters ? (
            <PanelSection title={t("physicalParameters")}>
              <DiffCategory
                hideKeys
                diff={diff.physical_parameters}
                config={physicalParametersAttributesConfig}
              />
            </PanelSection>
          ) : null}
          {diff.material_chemistry_overrides &&
          diff.material_chemistry_overrides.length > 0 ? (
            <PanelSection title={t("materialChemistryOverrides")}>
              {diff.material_chemistry_overrides.map((chemistry) => {
                const sideLookups =
                  chemistry.previous === null
                    ? sourceBaseLookups
                    : chemistry.updated === null
                      ? targetBaseLookups
                      : null;
                const representative = chemistry.updated ?? chemistry.previous;
                const base =
                  sideLookups !== null && representative
                    ? (sideLookups.materialChemistries.get(
                        materialChemistryKey(
                          representative.material_name,
                          representative.chemical_element_name
                        )
                      ) ?? null)
                    : null;
                return (
                  <MaterialChemistryDiffItem
                    key={chemistry.key}
                    diff={chemistry}
                    base={base}
                  />
                );
              })}
            </PanelSection>
          ) : null}
          {diff.fixed_mix_overrides && diff.fixed_mix_overrides.length > 0 ? (
            <PanelSection title={t("fixedMixOverrides")}>
              {diff.fixed_mix_overrides.map((fixedMix) => {
                const sideLookups =
                  fixedMix.previous === null
                    ? sourceBaseLookups
                    : fixedMix.updated === null
                      ? targetBaseLookups
                      : null;
                const representative = fixedMix.updated ?? fixedMix.previous;
                const base =
                  sideLookups !== null && representative
                    ? (sideLookups.fixedMixes.get(
                        representative.steel_grade_name
                      ) ?? null)
                    : null;
                return (
                  <FixedMixOverrideDiffItem
                    key={fixedMix.key}
                    diff={fixedMix}
                    base={base}
                  />
                );
              })}
            </PanelSection>
          ) : null}
          {diff.material_limit_overrides &&
          diff.material_limit_overrides.length > 0 ? (
            <PanelSection title={t("materialLimits")}>
              {diff.material_limit_overrides
                .filter(
                  (limit) =>
                    !isNameBasedLimitNullified(
                      limit.updated ?? limit.previous ?? null
                    )
                )
                .map((limit) => {
                  const sideLookups =
                    limit.previous === null
                      ? sourceBaseLookups
                      : limit.updated === null
                        ? targetBaseLookups
                        : null;
                  const representative = limit.updated ?? limit.previous;
                  const base =
                    sideLookups !== null && representative
                      ? (sideLookups.materialLimits.get(
                          materialLimitKey(
                            representative.chef_group_name,
                            representative.name
                          )
                        ) ?? null)
                      : null;
                  return (
                    <MaterialLimitDiffItem
                      key={limit.key}
                      diff={limit}
                      base={base}
                    />
                  );
                })}
            </PanelSection>
          ) : null}
        </Stack>
      ) : null}
      {diff.periods && diff.periods.length > 0
        ? diff.periods.map((periodDiff) => (
            <DifferencePanelPeriod
              key={periodDiff.name}
              periodDiff={periodDiff}
            />
          ))
        : null}
    </Stack>
  );
};

const useSideBaseLookups = (
  tenantName: string,
  searchId: number
): BaseLookups => {
  const searchQuery = useGetSearchQuery({ tenantName, searchId });
  const contextId = searchQuery.data?.context_id ?? null;
  const contextQuery = useGetSearchContextQuery(
    contextId === null ? skipToken : { tenantName, searchContextId: contextId }
  );
  const materialsId = contextQuery.data?.materials_id ?? null;
  const materialPhysicsId = contextQuery.data?.material_physics_id ?? null;
  const classesQuery = useListMaterialConstraintClassesQuery(
    materialsId === null || materialPhysicsId === null
      ? skipToken
      : {
          tenantName,
          materialMetadataSetId: materialsId,
          materialPhysicsSetId: materialPhysicsId,
        }
  );

  return useMemo<BaseLookups>(
    () => ({
      materialLimits: buildBaseMaterialLimitLookup(
        contextQuery.data,
        classesQuery.data
      ),
      materialChemistries: buildBaseMaterialChemistryLookup(contextQuery.data),
      fixedMixes: buildBaseFixedMixLookup(contextQuery.data),
    }),
    [contextQuery.data, classesQuery.data]
  );
};

const ResultBody = ({ targetSearchId, sourceSearchId }: Props) => {
  const tenantName = useTenant();
  const diffQuery = useGetNameBasedSearchDiffQuery(
    {
      tenantName,
      targetSearchId,
      sourceSearchId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const sourceBaseLookups = useSideBaseLookups(tenantName, sourceSearchId);
  const targetBaseLookups = useSideBaseLookups(tenantName, targetSearchId);
  const { t } = useTenantTranslation();

  if (diffQuery.isUninitialized || diffQuery.isLoading) {
    return (
      <Stack>
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="text" width={100} height={20} />
      </Stack>
    );
  }

  if (diffQuery.isError) {
    return (
      <Typography color="error">{t("errorLoadingSearchDifference")}</Typography>
    );
  }

  return (
    <DiffResults
      diff={diffQuery.data}
      sourceBaseLookups={sourceBaseLookups}
      targetBaseLookups={targetBaseLookups}
    />
  );
};

export const DifferencePanel = (props: Props) => {
  return (
    <Stack sx={{ px: 2, pt: 2, pb: 2 }}>
      <ResultBody {...props} />
    </Stack>
  );
};
